import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { API_URL } from '../utils/api';

const DataContext = createContext({
  data: {
    offers: null,
    roomTypes: null,
    users: null,
    offerSources: null
  },
  loading: {},
  errors: {},
  fetchData: () => {},
  refreshData: () => {},
  setData: () => {},
  stats: { reads: 0, writes: 0 },
  incrementReads: () => {},
  incrementWrites: () => {},
  resetStats: () => {}
});

// Sözleşme:
//   const { data, loading, errors, fetchData, refreshData } = useData();
//   errors[koleksiyon] = son okumanın hata metni (string) ya da undefined/null.
//   Okuma başarısız olursa data[koleksiyon] önceki dizi (yoksa []) kalır ve errors[koleksiyon] dolar;
//   başarılı okumada temizlenir. Ekranlar "boş liste" ile "yüklenemedi"yi errors ile ayırt eder.
export function useData() {
  return useContext(DataContext);
}

// Bu uçlar her zaman LİSTE döndürür. Sunucu hata verip { message } dönerse bu nesne state'e yazılmaz:
// ekranlar .map/.filter çağırırken çöküyordu (beyaz ekran).
const LIST_COLLECTIONS = new Set(['offers', 'roomTypes', 'users', 'offerSources', 'activityLogs']);

const NETWORK_ERROR = 'Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edip tekrar deneyin.';

// Yanıtı okur; başarısızsa ya da beklenen biçimde değilse Error fırlatır (mevcut veri korunur).
// Token'ı AuthContext'teki genel fetch sarmalayıcısı ekler (fetchSession.js); token parametresi geriye uyum için duruyor.
const loadCollection = async (collectionName, token) => {
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
  let response;
  try {
    response = await fetch(`${API_URL}/${collectionName}`, { headers });
  } catch {
    // fetch yalnızca ağ hatasında fırlatır ("Failed to fetch" vb.) → anlaşılır Türkçe metin
    throw new Error(NETWORK_ERROR);
  }
  let body = null;
  try { body = await response.json(); } catch { /* JSON olmayan yanıt */ }
  if (!response.ok) {
    throw new Error(body?.message || `Sunucu hatası (${response.status})`);
  }
  if (LIST_COLLECTIONS.has(collectionName) && !Array.isArray(body)) {
    throw new Error('Sunucudan liste bekleniyordu, farklı bir yanıt geldi.');
  }
  if (body === null || body === undefined) {
    throw new Error('Sunucudan boş yanıt geldi.');
  }
  // Teklifleri yeniden eskiye sırala
  if (collectionName === 'offers') {
    body.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }
  return body;
};

export function DataProvider({ children }) {
  const [data, setData] = useState({
    offers: null,
    roomTypes: null,
    users: null,
    offerSources: null
  });

  const [loading, setLoading] = useState({
    offers: false,
    roomTypes: false,
    users: false,
    offerSources: false
  });

  // Koleksiyon başına son okuma hatası (string) — başarılı okumada null
  const [errors, setErrors] = useState({});

  const [stats, setStats] = useState({ reads: 0, writes: 0 });

  // Aynı koleksiyon için üst üste istek giderse (ör. Gösterge Paneli + sezon seçici) yalnızca EN SON isteğin
  // sonucu yazılır: geç gelen eski yanıt yeni veriyi/hatayı ezmez, yükleniyor bayrağını erken kapatmaz.
  const requestSeq = useRef({});

  const incrementReads = useCallback((count = 1) => {
    setStats(prev => ({ ...prev, reads: prev.reads + count }));
  }, []);

  const incrementWrites = useCallback((count = 1) => {
    setStats(prev => ({ ...prev, writes: prev.writes + count }));
  }, []);

  const resetStats = useCallback(() => {
    setStats({ reads: 0, writes: 0 });
  }, []);

  // Ortak yükleyici: başarılıysa veriyi yazar ve hatayı temizler; hata olursa mevcut veriyi korur
  // (hiç yoksa liste uçlarında []) ve errors[koleksiyon]'a mesajı yazar.
  const load = useCallback(async (collectionName, token, label) => {
    const seq = (requestSeq.current[collectionName] || 0) + 1;
    requestSeq.current[collectionName] = seq;
    const isLatest = () => requestSeq.current[collectionName] === seq;

    setLoading(prev => ({ ...prev, [collectionName]: true }));
    try {
      const items = await loadCollection(collectionName, token);
      if (!isLatest()) return;
      setData(prev => ({ ...prev, [collectionName]: items }));
      setErrors(prev => (prev[collectionName] ? { ...prev, [collectionName]: null } : prev));
    } catch (error) {
      console.error(`${label} ${collectionName}:`, error);
      if (!isLatest()) return;
      setData(prev => {
        const current = prev[collectionName];
        const keep = LIST_COLLECTIONS.has(collectionName)
          ? (Array.isArray(current) ? current : [])
          : (current ?? null);
        return current === keep ? prev : { ...prev, [collectionName]: keep };
      });
      const message = (error && error.message) || 'Veriler yüklenemedi.';
      setErrors(prev => (prev[collectionName] === message ? prev : { ...prev, [collectionName]: message }));
    } finally {
      if (isLatest()) setLoading(prev => ({ ...prev, [collectionName]: false }));
    }
  }, []);

  // Veriyi çek (ekran açılışında)
  const fetchData = useCallback(
    (collectionName, token = null) => load(collectionName, token, 'Veri yüklenemedi:'),
    [load]
  );

  // Veriyi zorla yenile (Ekleme/Silme/Güncelleme sonrası)
  const refreshData = useCallback(
    (collectionName, token = null) => load(collectionName, token, 'Veri yenilenemedi:'),
    [load]
  );

  return (
    <DataContext.Provider value={{ data, loading, errors, fetchData, refreshData, setData, stats, incrementReads, incrementWrites, resetStats }}>
      {children}
    </DataContext.Provider>
  );
}
